import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { ShieldCheck, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function PlatformSignIn() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, admin } = usePlatformAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Already logged in → straight to dashboard
  React.useEffect(() => { if (admin) navigate('/platform-admin/dashboard'); }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { email, password };
      if (needsTotp) body.totp_code = totpCode;
      const res = await axios.post(`${API}/platform/auth/signin`, body);
      if (res.data?.requires_totp) {
        // Backend confirmed password but wants the 2FA code → switch to step 2.
        setNeedsTotp(true);
        setSubmitting(false);
        return;
      }
      login(res.data.token, res.data.admin);
      toast({ title: 'Welcome to Platform HQ', description: `Signed in as ${res.data.admin.name}` });
      navigate('/platform-admin/dashboard');
    } catch (err) {
      toast({
        title: 'Sign-in failed',
        description: err?.response?.data?.detail || 'Invalid credentials',
        variant: 'destructive',
      });
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setNeedsTotp(false);
    setTotpCode('');
    setPassword('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-slate-950" data-testid="platform-signin-page">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
           style={{ background: 'radial-gradient(circle at 30% 20%, #1E88E5 0, transparent 50%), radial-gradient(circle at 80% 70%, #2CA01C 0, transparent 50%)' }} />
      <Card className="w-full max-w-md relative bg-slate-900 border border-slate-800">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}>
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Platform HQ</h1>
              <p className="text-xs text-slate-400">Scalevisory · operating console</p>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-3 mb-5 leading-relaxed">
            {needsTotp
              ? 'Enter the 6-digit code from your authenticator app to finish signing in.'
              : 'Sign in with your Scalevisory credentials. This is a separate account from any HinesTime customer login.'}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!needsTotp && (
              <>
                <div>
                  <Label htmlFor="pl-email" className="text-slate-300">Email</Label>
                  <Input
                    id="pl-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="platform-signin-email"
                  />
                </div>
                <div>
                  <Label htmlFor="pl-pw" className="text-slate-300">Password</Label>
                  <Input
                    id="pl-pw" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="platform-signin-password"
                  />
                </div>
              </>
            )}
            {needsTotp && (
              <div>
                <Label htmlFor="pl-totp" className="text-slate-300">Authenticator code</Label>
                <Input
                  id="pl-totp" inputMode="numeric" pattern="\d{6}" maxLength={6} required autoFocus
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="mt-1 bg-slate-800 border-slate-700 text-white text-lg tracking-widest font-mono"
                  data-testid="platform-signin-totp"
                />
                <button type="button" onClick={startOver}
                        className="text-xs text-slate-400 hover:text-slate-200 mt-2 underline"
                        data-testid="platform-signin-startover">
                  Use a different account
                </button>
              </div>
            )}
            <Button
              type="submit" disabled={submitting || (needsTotp && totpCode.length !== 6)}
              className="w-full text-white font-semibold"
              style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
              data-testid="platform-signin-submit"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in…</>) : (needsTotp ? 'Verify & continue' : 'Sign in to Platform HQ')}
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-6 text-center">
            Authorized Scalevisory personnel only. All access is logged.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
